// src/pages/T321RulesetRequest.jsx
// T321 Ruleset Access Request — copy of the T178 RulesetRequest form adapted for T321 (v2).
// All 4 T321 permission groups are eligible. ONE approval PER SELECTED GROUP, each with its own
// PAM-T321-<8 hex> id (same fan-out as the T321 Individual flow):
//   Base / Tools          → CTPS only
//   ARVT                  → L7+ leader → CTPS
//   Tools (PML Check)     → SPS Learning Tech (manual PML check) → L7+ leader → CTPS
// Rule detail and use case are shared across groups. Submits op createT321RulesetApproval
// (once per group, with rulesetBucket) via an inline fetch to /validate; services/api.js is
// not changed.

import React, { useState, useEffect } from 'react';
import { BASE_PERMISSIONS, ARVT_PERMISSIONS, TOOLS_PERMISSIONS, bucketOf, BUCKETS } from '../data/t321PermissionCatalog';
import { lookupManager, lookupT321Leader, checkLeaderLevel } from '../services/api';
import { getAccessToken } from '../services/auth';

// Viewer link built from the site the request is submitted on (pre-prod → pre-prod, prod → prod).
const viewerUrlFor = (requestId) => `${window.location.origin}/viewer?id=${encodeURIComponent(requestId || '')}`;

// Every T321 permission tagged with its category and bucket (same tagging as T321Request.jsx).
const ALL_PERMISSIONS = [
    ...BASE_PERMISSIONS.map(p => ({ ...p, category: 'base' })),
    ...ARVT_PERMISSIONS.map(p => ({ ...p, category: 'arvt' })),
    ...TOOLS_PERMISSIONS.map(p => ({ ...p, category: 'tools' })),
].map(p => ({ ...p, bucket: bucketOf(p) }));
const PERMS_BY_BUCKET = BUCKETS.reduce((m, b) => { m[b.id] = ALL_PERMISSIONS.filter(p => p.bucket === b.id); return m; }, {});

// Group names for the Ruleset flow (BUCKETS titles carry Individual-flow routing text).
const GROUP_NAMES = {
    base: 'Base Permissions',
    arvt: 'ARVT Permissions',
    tools: 'Tools Permissions',
    tools_pml: 'Tools Permissions (PML Check)',
};
// Approval route of each group's ruleset approval.
const GROUP_ROUTING = {
    base: 'CTPS only',
    arvt: 'L7+ leader → CTPS',
    tools: 'CTPS only',
    tools_pml: 'SPS Learning Tech → L7+ leader → CTPS',
};
// Groups whose ruleset approval needs an L7+ leader (each gets its own leader field).
const LEADER_BUCKETS = ['arvt', 'tools_pml'];
const LEADER_LEVEL = 7;
const PML_NOTE = 'The PML training check is done manually by SPS Learning Tech, as the first approval stage.';

// Same use-case rules as the T321 Individual flow: >= 25 words and no unfilled [..] placeholder.
const MIN_USE_CASE_WORDS = 25;
const countWords = (text) =>
    (text || '').trim().split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w)).length;
const countPlaceholders = (text) => ((text || '').match(/\[[^\]]*\]/g) || []).length;
const getUseCaseIssue = (text) => {
    if (!(text || '').trim()) return 'Use-Case is required.';
    const n = countPlaceholders(text);
    if (n > 0) return `Replace the ${n} remaining [bracketed] field${n === 1 ? '' : 's'} with your details.`;
    const w = countWords(text);
    if (w < MIN_USE_CASE_WORDS) return `Use-Case needs at least ${MIN_USE_CASE_WORDS} words (currently ${w}).`;
    return '';
};
const RULESET_USECASE_TEMPLATE = 'As a [business title] working in [org name], [function and process], I need rule-based access to [T321 permission(s)] for [who the rule covers, e.g., all reports of a manager / a job family and code] so that they can [task or process, e.g., work SIV or KYC queues]. Region(s): [NA / EU / FE]. Impact if not granted: [what cannot be done / which process or metric is affected].';

const ROUTING_NOTE = 'Each permission group you select becomes its own approval. Base and Tools are reviewed by the CTPS Permissions team only. ARVT needs an L7+ leader, then CTPS. Tools (PML Check) goes to SPS Learning Tech (manual PML check), then an L7+ leader, then CTPS.';

// Vertical Stepper Component (steps depend on whether a leader is needed)
const VerticalStepper = ({ steps, current }) => {
    const currentIdx = steps.findIndex(s => s.key === current);
    return (
        <ol aria-label="Request progress" style={{ display: 'flex', flexDirection: 'column', gap: '0', listStyle: 'none', margin: 0, padding: 0 }}>
            {steps.map((s, idx) => {
                const isCompleted = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                return (
                    <li key={s.key} aria-current={isCurrent ? 'step' : undefined} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', fontWeight: '700', flexShrink: 0,
                                background: isCompleted ? '#22c55e' : isCurrent ? '#ff9900' : '#e9ecef',
                                color: isCompleted ? '#fff' : isCurrent ? '#000' : '#6b7280',
                                border: isCurrent ? '3px solid #ffcc80' : '2px solid transparent'
                            }}>
                                {isCompleted ? '✓' : idx + 1}
                            </div>
                            {idx < steps.length - 1 && (
                                <div style={{ width: '2px', height: '28px', background: isCompleted ? '#22c55e' : '#e2e6ea', margin: '4px 0' }} />
                            )}
                        </div>
                        <div style={{ paddingTop: '6px' }}>
                            <div style={{ fontSize: '14px', fontWeight: isCurrent ? '700' : '500', color: isCurrent ? '#111' : isCompleted ? '#333' : '#6b7280' }}>{s.label}</div>
                            <div style={{ fontSize: '12px', color: isCurrent ? '#555' : '#6b7280', marginTop: '1px' }}>{s.desc}</div>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};

// Visually hidden but read by screen readers.
const srOnly = { position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '13px' };
const backBtnStyle = { padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ccc', background: '#fff', color: '#444', marginBottom: '16px' };
const primaryBtnStyle = { width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' };
const errorBoxStyle = { marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#b91c1c' };
const warnBoxStyle = { marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' };

const RoutingNote = () => (
    <div role="note" style={{ padding: '10px 14px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '13px', color: '#1e3a8a', marginBottom: '16px' }}>
        ℹ️ {ROUTING_NOTE}
    </div>
);

// One L7+ leader field (same checks as the T321 Individual leader step):
// self / too_low / not_in_system are hard blocks; not_found (CARDS outage) is a soft-allow.
const LeaderField = ({ bucketId, level, state, onInput, onCheck }) => {
    const id = `t321rs-leader-${bucketId}`;
    const s = state || {};
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <label htmlFor={id} style={{ ...labelStyle, fontSize: '14px' }}>
                👔 {GROUP_NAMES[bucketId]} leader (L{level}+) <span style={{ color: '#b91c1c' }} aria-hidden="true">*</span><span style={srOnly}> (required)</span>
            </label>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>Approval route: {GROUP_ROUTING[bucketId].replace('L7+', `L${level}+`)}</div>
            {bucketId === 'tools_pml' && <div style={{ ...warnBoxStyle, marginTop: 0, marginBottom: '8px' }}>ℹ️ {PML_NOTE}</div>}
            <div style={{ display: 'flex', gap: '8px' }}>
                <input id={id} type="text" value={s.input || ''} aria-describedby={`${id}-status`}
                    onChange={(e) => onInput(bucketId, e.target.value)}
                    placeholder={`Enter an L${level}+ leader alias`} style={{ ...inputStyle, flex: 1 }} />
                <button type="button" onClick={() => onCheck(bucketId)} disabled={!(s.input || '').trim() || s.checking}
                    style={{ padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: s.checking ? 'wait' : 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                    {s.checking ? 'Checking...' : 'Set'}
                </button>
            </div>
            <div id={`${id}-status`} aria-live="polite">
                {s.status === 'ok' && <div style={{ marginTop: '6px', fontSize: '12px', color: '#166534' }}>✅ {s.alias}{s.jobLevel ? ` (L${s.jobLevel})` : ''} accepted</div>}
                {s.status === 'not_found' && <div style={warnBoxStyle}>⚠️ Could not verify <strong>{s.alias}</strong> right now (directory lookup unavailable). You may proceed, but make sure the leader is L{level} or above, otherwise the request will be rejected.</div>}
                {s.status === 'self' && <div style={errorBoxStyle}>❌ You cannot select yourself as your approval leader. Enter a different L{level}+ leader.</div>}
                {s.status === 'too_low' && <div style={errorBoxStyle}>❌ <strong>{s.checkedAlias}</strong> is L{s.jobLevel}, below the required L{level}. Enter an L{level}+ leader.</div>}
                {s.status === 'not_in_system' && <div style={errorBoxStyle}>❌ <strong>{s.checkedAlias}</strong> is not a valid Amazon employee alias.</div>}
                {s.status === 'error' && <div style={warnBoxStyle}>⚠️ Could not verify the leader right now. Please try again.</div>}
                {!s.status && (s.input || '').trim() && <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>Select “Set” to check this leader.</div>}
            </div>
        </div>
    );
};

const T321RulesetRequest = ({ userAlias }) => {
    const [step, setStep] = useState('setup');

    // Setup (auto-populated)
    const [userDepartment, setUserDepartment] = useState('');
    const [userJobFamily, setUserJobFamily] = useState('');
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);

    // Permissions
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [collapsed, setCollapsed] = useState({ base: false, arvt: false, tools: true, tools_pml: true });

    // Details (shared across groups)
    const [ruleDetail, setRuleDetail] = useState('');
    const [reportsOf, setReportsOf] = useState('');
    const [usersLogins, setUsersLogins] = useState('');
    const [toolsRequested, setToolsRequested] = useState('');
    const [costCenter, setCostCenter] = useState('');
    const [jobFamily, setJobFamily] = useState('');
    const [jobCode, setJobCode] = useState('');
    const [useCase, setUseCase] = useState('');

    // Leaders (separate ARVT / PML fields)
    const [leaders, setLeaders] = useState({});          // { bucketId: { input, alias, status, jobLevel, checking, checkedAlias } }
    const [leaderLevel, setLeaderLevel] = useState(LEADER_LEVEL);
    const [autoDetect, setAutoDetect] = useState({});     // { running, done, message }

    // Submit
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [results, setResults] = useState([]);           // [{ bucket, requestId, ok, approvalsUrl, error }]

    const requester = (userAlias || '').toLowerCase();

    // Auto-detect department on mount
    useEffect(() => {
        const loadDepartment = async () => {
            if (!userAlias || isDepartmentLoading) return;
            setIsDepartmentLoading(true);
            try {
                const result = await lookupManager(userAlias, 6);
                if (result && result.userDepartment) setUserDepartment(result.userDepartment);
                if (result && result.userJobFamily) setUserJobFamily(result.userJobFamily);
            } catch (err) {
                console.warn('Department lookup failed:', err);
            } finally {
                setIsDepartmentLoading(false);
            }
        };
        loadDepartment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAlias]);

    const togglePermission = (permId) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(permId)) next.delete(permId);
            else next.add(permId);
            return next;
        });
    };

    const selectedObjects = ALL_PERMISSIONS.filter(p => selectedPermissions.has(p.id));
    const selectedBucketIds = BUCKETS.map(b => b.id).filter(id => selectedObjects.some(p => p.bucket === id));
    const leaderBuckets = selectedBucketIds.filter(b => LEADER_BUCKETS.includes(b));
    const needsLeaders = leaderBuckets.length > 0;
    const useCaseIssue = getUseCaseIssue(useCase);
    const autoTools = selectedObjects.map(p => p.name).join(', ');
    const detailsComplete = !!(ruleDetail.trim() && (toolsRequested.trim() || autoTools) && !useCaseIssue);
    const leaderAccepted = (b) => {
        const s = leaders[b] || {};
        return !!(s.alias && (s.status === 'ok' || s.status === 'not_found'));
    };
    const allLeadersOk = leaderBuckets.every(leaderAccepted);

    const steps = [
        { key: 'setup', label: 'Setup', desc: 'Requester & tenant' },
        { key: 'perms', label: 'Permissions', desc: 'Select access' },
        { key: 'details', label: 'Details', desc: 'Rule & justification' },
        ...(needsLeaders ? [{ key: 'leaders', label: 'Leaders', desc: 'L7+ approval' }] : []),
        { key: 'submit', label: 'Submit', desc: 'Review & send' },
    ];
    const stepAfterDetails = needsLeaders ? 'leaders' : 'submit';

    // ── Leaders ──
    const setLeaderInput = (b, value) => setLeaders(prev => ({ ...prev, [b]: { input: value } }));
    const runLeaderCheck = async (b, aliasArg, level) => {
        const alias = (aliasArg || '').split('@')[0].trim().toLowerCase();
        if (!alias) return;
        setLeaders(prev => ({ ...prev, [b]: { ...(prev[b] || {}), input: alias, checking: true, status: undefined, alias: '' } }));
        const res = await checkLeaderLevel(alias, level, requester);
        setLeaders(prev => {
            const cur = prev[b] || {};
            if (cur.input !== alias) return prev;   // edited while checking — ignore the stale result
            const accepted = res.status === 'ok' || res.status === 'not_found';
            return { ...prev, [b]: { input: alias, checking: false, status: res.status, jobLevel: res.jobLevel, checkedAlias: alias, alias: accepted ? alias : '' } };
        });
    };
    // Pre-fill every empty leader field from ONE lookup (it also returns targetLevel, which
    // honours the backend's T321 leader-level override), then check each field.
    const runAutoDetect = async () => {
        if (!requester || autoDetect.running) return;
        setAutoDetect({ running: true });
        try {
            const r = await lookupT321Leader(requester, LEADER_LEVEL);
            const t = Number(r && r.targetLevel);
            const level = (Number.isInteger(t) && t >= 1 && t < LEADER_LEVEL) ? t : LEADER_LEVEL;
            setLeaderLevel(level);
            const found = r && r.success && r.leaderAlias ? String(r.leaderAlias).toLowerCase() : '';
            setAutoDetect({ done: true, message: found ? `Suggested leader: ${found}. Change it if a different leader should approve.` : 'Could not auto-detect a leader. Please enter one.' });
            if (found) {
                for (const b of leaderBuckets) {
                    if (!((leaders[b] || {}).input || '').trim()) await runLeaderCheck(b, found, level);
                }
            }
        } catch (err) {
            setAutoDetect({ done: true, message: 'Could not auto-detect a leader. Please enter one.' });
        }
    };
    useEffect(() => {
        if (step === 'leaders' && !autoDetect.done && !autoDetect.running) runAutoDetect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // ── Submit: one approval per selected group, sequentially, each with its own id ──
    const submitBuckets = async (bucketIds) => {
        setIsSubmitting(true);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
        const token = getAccessToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const usedIds = new Set(results.map(r => r.requestId));
        const makeRequestId = () => {
            let id;
            do {
                const b = new Uint8Array(4);
                window.crypto.getRandomValues(b);
                id = 'PAM-T321-' + Array.from(b, x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
            } while (usedIds.has(id));
            usedIds.add(id);
            return id;
        };
        const out = [];
        for (const b of bucketIds) {
            const perms = selectedObjects.filter(p => p.bucket === b);
            const names = perms.map(p => p.name);
            const requestId = makeRequestId();
            let title = `T321 Ruleset Access (${GROUP_NAMES[b]}) - ${names.join(', ')}`;
            if (title.length > 150) title = title.slice(0, 147) + '...';
            const payload = {
                operation: 'createT321RulesetApproval',
                requestId,
                rulesetBucket: b,
                requesterAlias: userAlias,
                title,
                viewerUrl: viewerUrlFor(requestId),
                formData: {
                    tenant: 'T321',
                    rulesetBucket: b,
                    selectedPermissionNames: names,
                    permissionLinks: perms.map(p => ({ name: p.name, link: p.link })),
                    permissionBuckets: [b],
                    toolsRequested: toolsRequested.trim() || names.join(', '),
                    ruleDetail: ruleDetail.trim(),
                    reportsOf: reportsOf.trim(),
                    usersLogins: usersLogins.trim(),
                    costCenter: costCenter.trim(),
                    jobFamily: jobFamily.trim(),
                    jobCode: jobCode.trim(),
                    useCase: useCase.trim()
                }
            };
            if (LEADER_BUCKETS.includes(b)) payload.l7ManagerAlias = (leaders[b] || {}).alias;
            try {
                const response = await fetch(`${API_BASE_URL}/validate`, { method: 'POST', headers, body: JSON.stringify(payload) });
                const raw = await response.json();
                const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;
                if (!response.ok || result.error) throw new Error(result.error || `API error: ${response.status}`);
                out.push({ bucket: b, requestId: result.requestId || requestId, ok: true, approvalsUrl: result.approvalsUrl });
            } catch (err) {
                out.push({ bucket: b, requestId, ok: false, error: err.message });
            }
        }
        // Retried groups replace their earlier failed row.
        setResults(prev => [...prev.filter(r => !bucketIds.includes(r.bucket)), ...out]
            .sort((x, y) => BUCKETS.findIndex(k => k.id === x.bucket) - BUCKETS.findIndex(k => k.id === y.bucket)));
        setIsSubmitting(false);
    };

    const submitted = results.length > 0;
    const failedBuckets = results.filter(r => !r.ok).map(r => r.bucket);
    const okCount = results.filter(r => r.ok).length;

    // ════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════
    return (
        <div style={{ width: '100%', padding: '0 40px 96px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ marginBottom: '16px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', margin: '0 0 2px 0' }}>📋 T321 Ruleset Access Request</h1>
                <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>Request rule-based access to Tenant 321 permissions (Base, ARVT, Tools). Each permission group becomes its own approval.</p>
            </div>

            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                {/* Stepper */}
                <div style={{ width: '170px', flexShrink: 0 }}>
                    <div style={{ position: 'sticky', top: '72px' }}>
                        <VerticalStepper steps={steps} current={step} />
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>

                    {/* ══════ Setup ══════ */}
                    {step === 'setup' && (
                        <div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }} aria-hidden="true">👤</span> <strong>Requester: {userAlias}</strong> — Authenticated via Midway
                            </div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0f8ff', border: '1px solid #b8d4f0', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }} aria-hidden="true">🏢</span> <strong>Department:</strong> {isDepartmentLoading ? '⏳ Loading...' : (userDepartment || '—')} | <strong>Job Family:</strong> {userJobFamily || '—'}
                            </div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea', marginBottom: '16px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }} aria-hidden="true">🏷</span> <strong>Tenant/Platform:</strong> <span style={{ padding: '2px 10px', borderRadius: '6px', background: '#e2e6ea', color: '#333', fontWeight: '600' }}>T321</span> <span style={{ fontSize: '12px', color: '#666' }}>(pre-filled)</span>
                            </div>
                            <RoutingNote />
                            <button type="button" onClick={() => setStep('perms')} style={primaryBtnStyle}>
                                Continue to Permissions →
                            </button>
                        </div>
                    )}

                    {/* ══════ Permissions ══════ */}
                    {step === 'perms' && (
                        <div>
                            <button type="button" onClick={() => setStep('setup')} style={backBtnStyle}>
                                ← Back to Setup
                            </button>

                            {BUCKETS.map(b => {
                                const perms = PERMS_BY_BUCKET[b.id] || [];
                                const isCollapsed = !!collapsed[b.id];
                                const picked = perms.filter(p => selectedPermissions.has(p.id)).length;
                                return (
                                    <div key={b.id} style={{ marginBottom: '16px' }}>
                                        <button type="button" aria-expanded={!isCollapsed}
                                            onClick={() => setCollapsed(prev => ({ ...prev, [b.id]: !prev[b.id] }))}
                                            style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px', background: b.bgColor, border: `1px solid ${b.borderColor}`, marginBottom: '8px', fontSize: '14px' }}>
                                            <span aria-hidden="true">{isCollapsed ? '▶' : '▼'}</span>
                                            <span style={{ fontSize: '15px' }} aria-hidden="true">{b.icon}</span>
                                            <strong style={{ color: '#1f2937' }}>{GROUP_NAMES[b.id] || b.title}</strong>
                                            <span style={{ fontSize: '12px', color: '#555' }}>({perms.length}{picked ? `, ${picked} selected` : ''})</span>
                                            <span style={{ fontSize: '12px', color: '#555', marginLeft: 'auto' }}>{GROUP_ROUTING[b.id]}</span>
                                        </button>
                                        {b.id === 'tools_pml' && !isCollapsed && (
                                            <div role="note" style={{ ...warnBoxStyle, marginTop: 0, marginBottom: '8px' }}>ℹ️ {PML_NOTE}</div>
                                        )}
                                        {!isCollapsed && perms.map(perm => {
                                            const checked = selectedPermissions.has(perm.id);
                                            const inputId = `t321rs-${perm.id}`;
                                            return (
                                                <div key={perm.id}
                                                    style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px', background: checked ? b.bgColor : '#fff' }}>
                                                    <input id={inputId} type="checkbox" checked={checked} onChange={() => togglePermission(perm.id)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                                                    <label htmlFor={inputId} style={{ flex: 1, cursor: 'pointer' }}>
                                                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#111' }}>
                                                            {perm.name}
                                                            {perm.tool && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#555', fontWeight: '400' }}>{perm.tool}</span>}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>{perm.description}</div>
                                                    </label>
                                                    {perm.link && (
                                                        <a href={perm.link} target="_blank" rel="noopener noreferrer" aria-label={`View ${perm.name} on permissions.amazon.com (opens in a new tab)`} style={{ fontSize: '12px', color: '#0066a1', textDecoration: 'none' }}>View ↗</a>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}

                            {/* Selected count + Continue */}
                            {selectedPermissions.size > 0 && (
                                <div style={{ position: 'sticky', bottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#fff', border: '1px solid #22c55e', boxShadow: '0 -2px 10px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                                        ✅ {selectedPermissions.size} permission(s) selected — {selectedBucketIds.length} approval{selectedBucketIds.length === 1 ? '' : 's'} (one per group)
                                    </span>
                                    <button type="button" onClick={() => setStep('details')}
                                        style={{ padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                        Continue to Details →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ══════ Details (shared by every group) ══════ */}
                    {step === 'details' && (
                        <div>
                            <button type="button" onClick={() => setStep('perms')} style={backBtnStyle}>
                                ← Back to Permissions
                            </button>

                            <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111', margin: '0 0 4px' }}>📝 Ruleset Details</h2>
                            {selectedBucketIds.length > 1 && (
                                <div style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>These details are used for all {selectedBucketIds.length} approvals.</div>
                            )}

                            {/* Rule Detail */}
                            <div style={{ marginBottom: '14px', marginTop: '12px' }}>
                                <label htmlFor="t321rs-rule" style={labelStyle}>Rule Detail <span style={{ color: '#b91c1c' }} aria-hidden="true">*</span><span style={srOnly}> (required)</span></label>
                                <textarea id="t321rs-rule" required aria-required="true" value={ruleDetail} onChange={(e) => setRuleDetail(e.target.value)}
                                    placeholder="Describe the rule to apply (e.g., 'All reports of <manager alias> in <team> should have these permissions')"
                                    rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>

                            {/* Reports Of */}
                            <div style={{ marginBottom: '14px' }}>
                                <label htmlFor="t321rs-reports" style={labelStyle}>Reports of (specify if only direct reports) (optional)</label>
                                <input id="t321rs-reports" type="text" value={reportsOf} onChange={(e) => setReportsOf(e.target.value)}
                                    placeholder="e.g., manager alias (direct reports only)" style={inputStyle} />
                            </div>

                            {/* Users Logins */}
                            <div style={{ marginBottom: '14px' }}>
                                <label htmlFor="t321rs-users" style={labelStyle}>Users Logins (optional)</label>
                                <textarea id="t321rs-users" value={usersLogins} onChange={(e) => setUsersLogins(e.target.value)}
                                    placeholder="Enter user logins (comma or newline separated) — or leave blank if the rule covers everyone"
                                    rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>

                            {/* Tools/Functions Requested — pre-filled from selected permissions */}
                            <div style={{ marginBottom: '14px' }}>
                                <label htmlFor="t321rs-tools" style={labelStyle}>Tool(s)/Function(s) Requested <span style={{ color: '#b91c1c' }} aria-hidden="true">*</span><span style={srOnly}> (required)</span></label>
                                <input id="t321rs-tools" type="text" value={toolsRequested || autoTools} onChange={(e) => setToolsRequested(e.target.value)}
                                    aria-describedby="t321rs-tools-hint" style={{ ...inputStyle, background: '#f8f9fa' }} />
                                <div id="t321rs-tools-hint" style={{ fontSize: '11px', color: '#555', marginTop: '3px' }}>Auto-filled from selected permissions (each approval lists its own group's permissions). Edit if needed.</div>
                            </div>

                            {/* Cost Center */}
                            <div style={{ marginBottom: '14px' }}>
                                <label htmlFor="t321rs-cc" style={labelStyle}>Cost Center (Phonetool) (optional)</label>
                                <input id="t321rs-cc" type="text" value={costCenter} onChange={(e) => setCostCenter(e.target.value)}
                                    placeholder="e.g., 1234" style={inputStyle} />
                            </div>

                            {/* Job Family + Job Code */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                <div>
                                    <label htmlFor="t321rs-jf" style={labelStyle}>Job Family (optional)</label>
                                    <input id="t321rs-jf" type="text" value={jobFamily} onChange={(e) => setJobFamily(e.target.value)}
                                        placeholder="e.g., PRGMGT" style={inputStyle} />
                                </div>
                                <div>
                                    <label htmlFor="t321rs-jc" style={labelStyle}>Job Code (optional)</label>
                                    <input id="t321rs-jc" type="text" value={jobCode} onChange={(e) => setJobCode(e.target.value)}
                                        placeholder="e.g., P04" style={inputStyle} />
                                </div>
                            </div>

                            {/* Use Case (business justification) */}
                            <div style={{ marginBottom: '16px' }}>
                                <label htmlFor="t321rs-usecase" style={labelStyle}>Use-Case (business justification) <span style={{ color: '#b91c1c' }} aria-hidden="true">*</span><span style={srOnly}> (required)</span></label>
                                <textarea id="t321rs-usecase" required aria-required="true" aria-invalid={!!(useCase.trim() && useCaseIssue)}
                                    aria-describedby="t321rs-usecase-status" value={useCase} onChange={(e) => setUseCase(e.target.value)}
                                    placeholder="Describe the business need. Use the template below and replace every [bracketed] field."
                                    rows={5} style={{ ...inputStyle, fontSize: '14px', lineHeight: '1.5', resize: 'vertical' }} />
                                <div id="t321rs-usecase-status" aria-live="polite" style={{ marginTop: '6px', fontSize: '12px', color: useCaseIssue ? '#b91c1c' : '#166534' }}>
                                    {useCase.trim()
                                        ? (useCaseIssue ? `⚠️ ${useCaseIssue}` : `✅ ${countWords(useCase)} words`)
                                        : `At least ${MIN_USE_CASE_WORDS} words, with no [bracketed] fields left.`}
                                </div>
                                {!useCase.trim() && (
                                    <div style={{ marginTop: '8px', padding: '10px 14px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '12px', color: '#444', lineHeight: '1.7' }}>
                                        <strong>Template:</strong> {RULESET_USECASE_TEMPLATE}
                                        <div style={{ marginTop: '6px' }}>
                                            <button type="button" onClick={() => setUseCase(RULESET_USECASE_TEMPLATE)}
                                                style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #ccc', background: '#fff', color: '#333' }}>
                                                Use this template
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button type="button" onClick={() => setStep(stepAfterDetails)} disabled={!detailsComplete}
                                style={{ ...primaryBtnStyle, cursor: detailsComplete ? 'pointer' : 'not-allowed', opacity: detailsComplete ? 1 : 0.5 }}>
                                {needsLeaders ? 'Continue to Leaders →' : 'Continue to Review →'}
                            </button>
                            {!detailsComplete && (
                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>Rule Detail, Tool(s)/Function(s) and a valid Use-Case are required.</div>
                            )}
                        </div>
                    )}

                    {/* ══════ Leaders (only for ARVT / Tools PML) ══════ */}
                    {step === 'leaders' && needsLeaders && (
                        <div>
                            <button type="button" onClick={() => setStep('details')} style={backBtnStyle}>
                                ← Back to Details
                            </button>
                            <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111', margin: '0 0 8px' }}>👔 Leadership Approval (L{leaderLevel}+)</h2>
                            <div style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
                                Each group below has its own leader field and its own approval. You can enter the same leader for both.
                            </div>
                            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '14px', fontSize: '13px', color: '#991b1b' }}>
                                ⚠️ <strong>Important:</strong> Please make sure you select the correct leader. If the wrong leader is selected, the request will be rejected.
                            </div>
                            {autoDetect.running && <div style={{ padding: '8px 0', fontSize: '13px', color: '#0066a1' }}>⏳ Looking up your L{LEADER_LEVEL}+ leader…</div>}
                            {autoDetect.message && <div style={{ padding: '4px 0 12px', fontSize: '12px', color: '#555' }}>{autoDetect.message}</div>}
                            {leaderBuckets.map(b => (
                                <LeaderField key={b} bucketId={b} level={leaderLevel} state={leaders[b]}
                                    onInput={setLeaderInput} onCheck={(id) => runLeaderCheck(id, (leaders[id] || {}).input, leaderLevel)} />
                            ))}
                            <button type="button" onClick={() => setStep('submit')} disabled={!allLeadersOk}
                                style={{ ...primaryBtnStyle, cursor: allLeadersOk ? 'pointer' : 'not-allowed', opacity: allLeadersOk ? 1 : 0.5 }}>
                                Continue to Review →
                            </button>
                            {!allLeadersOk && <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>Set a valid L{leaderLevel}+ leader for each group above.</div>}
                        </div>
                    )}

                    {/* ══════ Submit ══════ */}
                    {step === 'submit' && (
                        <div>
                            {!submitted && (
                                <button type="button" onClick={() => setStep(needsLeaders ? 'leaders' : 'details')} style={backBtnStyle}>
                                    ← Back to {needsLeaders ? 'Leaders' : 'Details'}
                                </button>
                            )}

                            <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111', margin: '0 0 16px' }}>📋 Review & Submit</h2>

                            <RoutingNote />

                            {/* Summary */}
                            <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', marginBottom: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                                    <div><strong>Requester:</strong> {userAlias}</div>
                                    <div><strong>Department:</strong> {userDepartment || '—'}</div>
                                    <div><strong>Tenant:</strong> T321</div>
                                    <div><strong>Approvals:</strong> {selectedBucketIds.length} (one per group)</div>
                                    <div><strong>Cost Center:</strong> {costCenter || '—'}</div>
                                    <div><strong>Job Family:</strong> {jobFamily || '—'}</div>
                                    <div><strong>Job Code:</strong> {jobCode || '—'}</div>
                                    <div><strong>Reports of:</strong> {reportsOf || '—'}</div>
                                </div>
                                <div style={{ marginTop: '10px', fontSize: '13px' }}><strong>Tool(s)/Function(s):</strong> {toolsRequested.trim() || autoTools}</div>
                                <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Use Case:</strong> {useCase}</div>
                                <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Rule Detail:</strong> {ruleDetail}</div>
                                {usersLogins && <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Users:</strong> {usersLogins}</div>}
                            </div>

                            {/* One block per approval */}
                            {selectedBucketIds.map(b => {
                                const meta = BUCKETS.find(x => x.id === b) || {};
                                const res = results.find(r => r.bucket === b);
                                return (
                                    <div key={b} style={{ padding: '12px 16px', borderRadius: '8px', background: meta.bgColor, border: `1px solid ${meta.borderColor}`, marginBottom: '12px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                                            {meta.icon} {GROUP_NAMES[b]} — {GROUP_ROUTING[b].replace('L7+', `L${leaderLevel}+`)}
                                            {LEADER_BUCKETS.includes(b) && (leaders[b] || {}).alias ? ` (leader: ${(leaders[b] || {}).alias})` : ''}
                                        </div>
                                        {selectedObjects.filter(p => p.bucket === b).map(p => (
                                            <div key={p.id} style={{ fontSize: '12px', color: '#333', marginBottom: '2px' }}>• {p.name}</div>
                                        ))}
                                        {res && res.ok && (
                                            <div role="status" style={{ marginTop: '8px', fontSize: '13px', color: '#166534' }}>
                                                ✅ Submitted: <strong>{res.requestId}</strong>{' '}
                                                {res.approvalsUrl && <a href={res.approvalsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0066a1', fontWeight: '600' }}>View on Approvals ↗</a>}{' '}
                                                <a href={viewerUrlFor(res.requestId)} target="_blank" rel="noopener noreferrer" style={{ color: '#0066a1', fontWeight: '600' }}>View request ↗</a>
                                            </div>
                                        )}
                                        {res && !res.ok && (
                                            <div role="alert" style={errorBoxStyle}>❌ Not submitted: {res.error}</div>
                                        )}
                                    </div>
                                );
                            })}

                            {!submitted && (
                                <button type="button" onClick={() => submitBuckets(selectedBucketIds)} disabled={isSubmitting || (needsLeaders && !allLeadersOk)}
                                    style={{ width: '100%', padding: '16px', borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: isSubmitting ? 'wait' : 'pointer', border: 'none', background: '#15803d', color: '#fff' }}>
                                    {isSubmitting ? '⏳ Submitting...' : `🚀 Submit ${selectedBucketIds.length} Ruleset Access request${selectedBucketIds.length === 1 ? '' : 's'}`}
                                </button>
                            )}
                            {submitted && (
                                <div style={{ padding: '14px', borderRadius: '10px', background: failedBuckets.length ? '#fffbeb' : '#f0fdf4', border: `2px solid ${failedBuckets.length ? '#f59e0b' : '#22c55e'}`, textAlign: 'center' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '700', color: failedBuckets.length ? '#92400e' : '#166534' }}>
                                        {failedBuckets.length
                                            ? `${okCount} of ${results.length} approvals submitted. ${failedBuckets.length} group${failedBuckets.length === 1 ? '' : 's'} failed.`
                                            : `🎉 All ${okCount} approval${okCount === 1 ? '' : 's'} submitted successfully`}
                                    </div>
                                    {failedBuckets.length > 0 && (
                                        <button type="button" onClick={() => submitBuckets(failedBuckets)} disabled={isSubmitting}
                                            style={{ marginTop: '10px', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: isSubmitting ? 'wait' : 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                            {isSubmitting ? '⏳ Retrying...' : '🔁 Retry failed groups'}
                                        </button>
                                    )}
                                    {failedBuckets.length > 0 && (
                                        <div style={{ fontSize: '12px', color: '#555', marginTop: '6px' }}>Retrying sends only the failed groups, with new request IDs. Submitted approvals are not sent again.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default T321RulesetRequest;
